import { Box, Stack, Paper, Skeleton, Typography } from '@mui/material';
import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTaxReportQueries } from './hooks/useTaxReportsQueries';
import { useTaxReportService } from '../../services/tax-report.service';
import { useApiClient } from '../../services/api';
import { environment } from '../../config/environment';
import { PaymentResultBanner } from './components/PaymentResultBanner';
import { useUserPlan } from '../../hooks/useUserPlan';
import { useTransactionService, DetailsType } from '../../services/transaction.service';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { QUERY_KEYS } from '../../utils/queryKeys';
import { useIgnoredIssues } from '../smart-check/hooks/useIgnoredIssues';
import { useRecalculation } from '../../context/RecalculationStatusContext';
import RecalculationAlert from './components/RecalculationAlert';

// Components
import YearSelectorCards from './components/YearSelectorCards';
import UnifiedTaxSummaryBox from './components/UnifiedTaxSummaryBox';
import DownloadFilesBox from './components/DownloadFilesBox';
import TaxFreeIncomeBox from './components/TaxFreeIncomeBox';
import OtherTransactionsBox from './components/OtherTransactionsBox';
import PurchaseActionsBox from './components/PurchaseActionsBox';

// Before this year we allow kapso download, after (and including) we do not
const KAPSO_CUTOFF_YEAR = 2025;

const TaxReportPage: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
     const { status: recalculationStatus } = useRecalculation();
    const isRecalculating = !!recalculationStatus?.run || !!recalculationStatus?.new;
    const location = useLocation();
    const reactQueryClient = useQueryClient();
    const apiClient = useApiClient();
    
    // Payment status state
    const [paymentStatus, setPaymentStatus] = useState<'success' | 'cancel' | null>(null);
    const [paymentData, setPaymentData] = useState<any>(null);
    const [paymentError, setPaymentError] = useState<string | null>(null);
    
    // Payment verification tracking to prevent duplicates
    const verificationInProgress = useRef<string | null>(null);
    const verifiedSessions = useRef<Set<string>>(new Set());
    
    const { isTaxReportsEnabled, isLoadingPlan } = useUserPlan();
    // Use single server-side check for tax reports access
    
    // Only load stats/summaries AFTER plan is loaded and tax reports are enabled
    const canLoadYearData = !isLoadingPlan && isTaxReportsEnabled();

    const {
        taxReports,
        selectedYear,
        setSelectedYear,
        taxPersonalData,
        yearOptions,
        downloadPdf: handleDownloadPdf,
        downloadExcel: handleDownloadExcel,
        downloadKapsoPdf: handleDownloadKapsoPdf,
        isLoading,
        isCriticalLoading,
        isDownloadLoading,
    } = useTaxReportQueries();

    // Extract individual loading states for better UX
    const reportsLoading = taxReports.length === 0 && isLoading;

    const transactionService = useTransactionService();
    const taxReportService = useTaxReportService();

    // We no longer need to fetch stats for all years since year cards are simplified
    // Only keep the Smart Check issues query which is still needed for warning indicators

    // Get Smart Check issues for all years
    const { data: smartCheckIssues, isLoading: isLoadingSmartCheckIssues } = useQuery({
        queryKey: [QUERY_KEYS.ALL_TIME_ISSUES],
        queryFn: () => transactionService.getAllTimeIssueCount(DetailsType.ONLY_ISSUES),
    });

    // Get ignored issues from Firebase to exclude them from counts
    const { data: allIgnoredIssues } = useIgnoredIssues();

    // Process Smart Check issues and group by year, excluding ignored issues
    const getIssueCountByYear = (year: string): number => {
        if (!smartCheckIssues) return 0;
        
        let issueCount = 0;
        const costBasisIssues = smartCheckIssues.missingCostBasisTransactions || [];
        const missingPriceIssues = smartCheckIssues.missingPriceIncomeTransactions || [];
        
        // Count cost basis issues for this year (exclude ignored issues)
        for (const issue of costBasisIssues) {
            // Cost basis issues use sold_at field (can be array)
            const issueYear = new Date(Array.isArray(issue.sold_at) ? issue.sold_at[0] : issue.sold_at).getFullYear().toString();
            if (issueYear === year) {
                // Only count if not ignored
                const isIgnored = allIgnoredIssues && allIgnoredIssues[issue.id.toString()];
                if (!isIgnored) {
                    issueCount++;
                }
            }
        }
        
        // Count missing price issues for this year (exclude ignored issues)
        for (const issue of missingPriceIssues) {
            // Missing price issues use time field (no *1000 needed as it's already in milliseconds)
            const issueYear = new Date(issue.time).getFullYear().toString();
            if (issueYear === year) {
                // Only count if not ignored
                const isIgnored = allIgnoredIssues && allIgnoredIssues[issue.id.toString()];
                if (!isIgnored) {
                    issueCount++;
                }
            }
        }
        
        return issueCount;
    };

    // Calculate total issue count for selected year (both types)
    const totalIssueCountForSelectedYear = getIssueCountByYear(selectedYear);

    // Calculate separate issue counts by type for selected year
    const getCostBasisIssueCountByYear = (year: string): number => {
        if (!smartCheckIssues) return 0;
        
        let issueCount = 0;
        const costBasisIssues = smartCheckIssues.missingCostBasisTransactions || [];
        
        for (const issue of costBasisIssues) {
            // Use same logic as Smart Check: prioritize taxYear property, fall back to date calculation
            const issueYear = (issue.taxYear || new Date(Array.isArray(issue.sold_at) ? issue.sold_at[0] : issue.sold_at).getFullYear()).toString();
            if (issueYear === year) {
                const isIgnored = allIgnoredIssues && allIgnoredIssues[issue.id.toString()];
                if (!isIgnored) {
                    issueCount++;
                }
            }
        }
        
        return issueCount;
    };

    const getMissingPriceIssueCountByYear = (year: string): number => {
        if (!smartCheckIssues) return 0;
        
        let issueCount = 0;
        const missingPriceIssues = smartCheckIssues.missingPriceIncomeTransactions || [];
        
        for (const issue of missingPriceIssues) {
            // Use same logic as Smart Check: prioritize taxYear property, fall back to date calculation
            const issueYear = (issue.taxYear || new Date(issue.time).getFullYear()).toString();
            if (issueYear === year) {
                const isIgnored = allIgnoredIssues && allIgnoredIssues[issue.id.toString()];
                if (!isIgnored) {
                    issueCount++;
                }
            }
        }
        
        return issueCount;
    };

    const costBasisCountForSelectedYear = getCostBasisIssueCountByYear(selectedYear);
    const missingPriceCountForSelectedYear = getMissingPriceIssueCountByYear(selectedYear);

    // Calculate savings potential ONLY from cost basis issues (missing price issues don't have reliable savings data)
    const calculateSavingsPotentialForYear = (year: string): number => {
        if (!smartCheckIssues) return 0;
        
        let totalSavings = 0;
        const costBasisIssues = smartCheckIssues.missingCostBasisTransactions || [];
        
        // Only calculate savings from missing cost basis issues (they have reliable gain property)
        for (const issue of costBasisIssues) {
            // Use same logic as Smart Check: prioritize taxYear property, fall back to date calculation
            const issueYear = (issue.taxYear || new Date(Array.isArray(issue.sold_at) ? issue.sold_at[0] : issue.sold_at).getFullYear()).toString();
            if (issueYear === year) {
                const isIgnored = allIgnoredIssues && allIgnoredIssues[issue.id.toString()];
                if (!isIgnored && issue.gain !== undefined && issue.gain !== null) {
                    // Use absolute value of gain as potential tax savings
                    totalSavings += Math.abs(issue.gain);
                }
            }
        }
        
        return totalSavings;
    };

    const totalSavingsPotentialForSelectedYear = calculateSavingsPotentialForYear(selectedYear);

    // Conditional logging
    useEffect(() => {
        console.log(`[TaxReportPageV2] Year ${selectedYear}: Cost basis issues: ${costBasisCountForSelectedYear}, Missing price issues: ${missingPriceCountForSelectedYear} | Savings: ${totalSavingsPotentialForSelectedYear}`);
    }, [selectedYear, costBasisCountForSelectedYear, missingPriceCountForSelectedYear, totalSavingsPotentialForSelectedYear]);

    // Only fetch data for the SELECTED year (not all years at once)
    const selectedReport = taxReports.find(r => r.tax_year === selectedYear);
    
    // Summary query for selected year only
    const { data: reportSummary, isLoading: isLoadingSummary } = useQuery({
        queryKey: ['reportSummary', selectedYear, selectedReport?.id],
        queryFn: () => taxReportService.reportsFullExportSummary(selectedReport!.id),
        enabled: !!selectedReport?.id && canLoadYearData,
    });

    // Transaction stats query for selected year only
    const { data: transactionStats, isLoading: isLoadingStats } = useQuery({
        queryKey: ['transactionStats', selectedYear, selectedReport?.id],
        queryFn: () => transactionService.getStats(selectedYear, selectedReport!.id),
        enabled: !!selectedReport?.id && canLoadYearData,
    });

    const isLoadingSelectedYear = isLoadingSummary || isLoadingStats;
    
    // Show loading when:
    // 1. Initial tax reports are loading (reportsLoading)
    // 2. No years available yet (yearOptions.length === 0)
    // 3. Selected year data is still loading
    // 4. Recalculation is in progress (data might be stale)
    const shouldShowLoading = reportsLoading || yearOptions.length === 0 || isLoadingSelectedYear || isRecalculating;

    // With lazy loading, we only have current year's stats
    // Global transaction count is managed by the plan (currentTransactions from BetaApp)

    // Check if user can load details based on server-side calculation
    const canLoadDetails = !!(
        selectedYear &&
        transactionStats &&
        isTaxReportsEnabled()
    );

    // Calculate taxable income from summary data (same logic as TaxSummaryBox)
    const calculateTaxableIncome = (summaryData: any[]): number => {
        if (!summaryData || summaryData.length === 0) return 0;

        // Helper function to find a value in the nested structure with multi-language support
        const findValueByLabel = (group: any, targetLabels: string[]): number => {
            // Check direct entries
            for (const entry of group.entries || []) {
                if (entry.kv && entry.kv.label) {
                    // Check if the label matches any of the target labels
                    if (targetLabels.some(label => entry.kv.label === label)) {
                        return entry.kv.value;
                    }
                }
                // Recursively check nested groups
                if (entry.g) {
                    const nestedValue = findValueByLabel(entry.g, targetLabels);
                    if (nestedValue !== 0) {
                        return nestedValue;
                    }
                }
            }
            return 0;
        };

        let totalAmount = 0;
        
        // Process each summary data item
        for (const summary of summaryData) {
            if (summary.g) {
                // Find values for specific income types (support both German and English labels)
                const cryptoSalesGains = findValueByLabel(
                    summary.g, 
                    [
                        'Einkünfte aus privaten Veräußerungsgeschäften nach § 23 EStG', // German
                        'Private sale income according to Art. 23 German income tax act' // English
                    ]
                );
                
                const otherIncome = findValueByLabel(
                    summary.g, 
                    [
                        'Sonstige Einkünfte nach § 22 Nr. 3 EStG', // German
                        'Other income according to Art. 22 No. 3 German income tax act' // English
                    ]
                );
                
                const marginDerivativesGains = findValueByLabel(
                    summary.g, 
                    [
                        'Kapitalerträge aus Margin- und Derivategeschäften', // German
                        'Total gains from margin, derivatives, futures' // English
                    ]
                );

                totalAmount += cryptoSalesGains + otherIncome + marginDerivativesGains;
            }
        }
        
        return totalAmount;
    };

    // Extract income breakdown from summary data
    const extractIncomeBreakdown = (summaryData: any[]) => {
        if (!summaryData || summaryData.length === 0) return [];

        // Helper function to find a value in the nested structure with multi-language support
        const findValueByLabel = (group: any, targetLabels: string[]): number => {
            // Check direct entries
            for (const entry of group.entries || []) {
                if (entry.kv && entry.kv.label) {
                    // Check if the label matches any of the target labels
                    if (targetLabels.some(label => entry.kv.label === label)) {
                        return entry.kv.value;
                    }
                }
                // Recursively check nested groups
                if (entry.g) {
                    const nestedValue = findValueByLabel(entry.g, targetLabels);
                    if (nestedValue !== 0) {
                        return nestedValue;
                    }
                }
            }
            return 0;
        };

        let cryptoSalesGains = 0;
        let otherIncome = 0;
        let marginDerivativesGains = 0;
        
        // Process each summary data item to extract values
        for (const summary of summaryData) {
            if (summary.g) {
                cryptoSalesGains += findValueByLabel(
                    summary.g, 
                    [
                        'Einkünfte aus privaten Veräußerungsgeschäften nach § 23 EStG', // German
                        'Private sale income according to Art. 23 German income tax act' // English
                    ]
                );
                
                otherIncome += findValueByLabel(
                    summary.g, 
                    [
                        'Sonstige Einkünfte nach § 22 Nr. 3 EStG', // German
                        'Other income according to Art. 22 No. 3 German income tax act' // English
                    ]
                );
                
                marginDerivativesGains += findValueByLabel(
                    summary.g, 
                    [
                        'Kapitalerträge aus Margin- und Derivategeschäften', // German
                        'Total gains from margin, derivatives, futures' // English
                    ]
                );
            }
        }
        
        return [
            {
                label: 'Private Veräußerungsgeschäfte (§ 23 EStG)',
                amount: cryptoSalesGains,
                translationKey: 'taxReports.privateSalesIncome'
            },
            {
                label: 'Sonstige Einkünfte (§ 22 Nr. 3 EStG)',
                amount: otherIncome,
                translationKey: 'taxReports.otherIncome'
            },
            {
                label: 'Kapitalerträge aus Margin-/Derivategeschäften',
                amount: marginDerivativesGains,
                translationKey: 'taxReports.capitalGains'
            }
        ];
    };

    // Extract additional tax data from summary data
    const extractAdditionalTaxData = (summaryData: any[]) => {
        if (!summaryData || summaryData.length === 0) return { otherTransactions: [], taxFreeIncome: [] };

        // Helper function to find a value in the nested structure with multi-language support
        const findValueByLabel = (group: any, targetLabels: string[]): number => {
            // Check direct entries
            for (const entry of group.entries || []) {
                if (entry.kv && entry.kv.label) {
                    // Check if the label matches any of the target labels
                    if (targetLabels.some(label => entry.kv.label === label)) {
                        return entry.kv.value;
                    }
                }
                // Recursively check nested groups
                if (entry.g) {
                    const nestedValue = findValueByLabel(entry.g, targetLabels);
                    if (nestedValue !== 0) {
                        return nestedValue;
                    }
                }
            }
            return 0;
        };


        // Helper function to search for labels containing keywords
        const findValueByKeywords = (group: any, keywords: string[]): number => {
            // Check direct entries
            for (const entry of group.entries || []) {
                if (entry.kv && entry.kv.label) {
                    const label = entry.kv.label.toLowerCase();
                    if (keywords.some(keyword => label.includes(keyword.toLowerCase()))) {
                        return entry.kv.value;
                    }
                }
                // Recursively check nested groups
                if (entry.g) {
                    const nestedValue = findValueByKeywords(entry.g, keywords);
                    if (nestedValue !== 0) {
                        return nestedValue;
                    }
                }
            }
            return 0;
        };

        let donationsReceived = 0;
        let donationsSent = 0;
        let lostStolenAssets = 0;
        let nonTaxableSalesIncome = 0;
        let nonTaxableOtherIncome = 0;
        
        // Process each summary data item to extract values
        for (const summary of summaryData) {
            if (summary.g) {
                
                // Look for donations - search by keywords
                donationsReceived += findValueByKeywords(summary.g, ['spende erhalten', 'donation received', 'schenkung erhalten']);
                donationsSent += findValueByKeywords(summary.g, ['spende', 'donation', 'schenkung']);
                
                // Look for lost/stolen assets
                lostStolenAssets += findValueByKeywords(summary.g, ['verlust', 'gestohlen', 'diebstahl', 'verloren', 'stolen', 'lost']);
                
                // Look for non-taxable income - support both German and English labels
                nonTaxableSalesIncome += findValueByLabel(summary.g, [
                    'Nicht steuerbare Einkünfte aus privaten Veräußerungsgeschäften', // German
                    'Private sale income non taxable' // English
                ]);
                nonTaxableOtherIncome += findValueByLabel(summary.g, [
                    'Nicht steuerbare sonstige Einkünfte', // German
                    'Other income non taxable' // English
                ]);
            }
        }
        
        return {
            otherTransactions: [
                {
                    label: 'Donations received',
                    amount: donationsReceived,
                    translationKey: 'taxReports.donationsReceived'
                },
                {
                    label: 'Donations sent',
                    amount: donationsSent,
                    translationKey: 'taxReports.donationsSent'
                },
                {
                    label: 'Lost/stolen assets',
                    amount: lostStolenAssets,
                    translationKey: 'taxReports.lostStolen'
                }
            ],
            taxFreeIncome: [
                {
                    label: 'Non-taxable sales income',
                    amount: nonTaxableSalesIncome,
                    translationKey: 'taxReports.nonTaxableSales'
                },
                {
                    label: 'Non-taxable other income',
                    amount: nonTaxableOtherIncome,
                    translationKey: 'taxReports.nonTaxableOther'
                }
            ]
        };
    };

    // Handle navigation to Smart Check with all issues for selected year
    const handleNavigateToSmartCheck = () => {
        navigate(`/smart-check?year=${selectedYear}&dateStart=${selectedYear}-01-01&dateEnd=${selectedYear}-12-31`);
    };

    // Redirect to main CoinTracking plans page for purchase
    const handleBuyYear = useCallback(() => {
        // Payments will be handled here later
        window.location.href = 'https://cointracking.info/plans';
    }, []);

    // Redirect to main CoinTracking plans page for purchase (all years)
    const handleBuyAllYears = useCallback(() => {
        // Payments will be handled here later
        window.location.href = 'https://cointracking.info/plans';
    }, []);

    // Prepare simplified year data for the cards (memoized to prevent unnecessary re-renders)
    // Must be called before any conditional returns to avoid React Hooks violation
    const yearDataMap = useMemo(() => {
        const dataMap: Record<string, any> = {};
        yearOptions.forEach(year => {
            // Get Smart Check issue count for this year
            const issueCount = getIssueCountByYear(year);
            
            // With global plan and lazy loading:
            // - All years locked/unlocked together based on tax reports enabled
            // - Loading state only for selected year
            const isThisYearSelected = year === selectedYear;
            const yearIsLoading = isThisYearSelected ? isLoadingSelectedYear : false;
            
            dataMap[year] = {
                year,
                isPaid: isTaxReportsEnabled(), // Global lock/unlock status
                hasIssues: issueCount > 0,
                issueCount: issueCount,
                isLoading: yearIsLoading,
                isPlanLoading: isLoadingPlan, // Plan data loading state
            };
        });
        return dataMap;
    }, [
        yearOptions,
        selectedYear,
        smartCheckIssues,
        allIgnoredIssues,
        isLoadingSelectedYear,
        isLoadingSmartCheckIssues,
        isTaxReportsEnabled,
        isLoadingPlan
    ]);


    // Create stable callback functions to minimize useEffect dependencies
    const handlePaymentVerification = useCallback(async (sessionId: string) => {
        try {
            // Mark verification as in progress and add to verified sessions immediately
            verificationInProgress.current = sessionId;
            verifiedSessions.current.add(sessionId);
            console.log('[PaymentSuccess] Starting verification for session:', sessionId);
            
            // Verify payment with backend
            const response = await apiClient.get(
                `${environment.expressApiUrl}/api/payment/verify-session/${sessionId}`
            );
            
            setPaymentData(response.data);
            setPaymentStatus('success');
            console.log('[PaymentSuccess] Payment verification completed successfully');
            
            // Send email separately - don't let email errors affect payment status
            try {
                const getFeatures = (details: any) => {
                    const features = [];
                    const metadata = details.metadata;

                    // Handle multiple tax report purchases using indexed format (same as Firebase)
                    const yearCount = parseInt(metadata.yearCount) || 0;
                    if (yearCount > 0) {
                        // Process each year using indexed metadata
                        for (let i = 0; i < yearCount; i++) {
                            const year = metadata[`taxYear_${i}`];
                            const packageId = metadata[`packageId_${i}`];
                            const totalTransactions = metadata[`totalTransactions_${i}`];
                            
                            if (packageId && year) {
                                if (totalTransactions) {
                                    // Include transaction count if available
                                    features.push(
                                        t(`plansData.packages.${packageId}.short_description`, {
                                            year: year,
                                            transactions: parseInt(totalTransactions) || 0,
                                        })
                                    );
                                } else {
                                    // Fallback to name + year format  
                                    features.push(
                                        t(`plansData.packages.${packageId}.name`) + ' ' + year
                                    );
                                }
                            }
                            
                            // Check expert review for this specific year
                            if (metadata[`expertReview_${i}`] === 'true') {
                                features.push(t('plansData.addons.expertReview.name', 'Expert Check') + ' ' + year);
                            }
                        }
                    }
                    
                    // Fallback for single tax report purchases (backward compatibility)
                    else if (metadata.packageId && metadata.taxYear) {
                        features.push(
                            t(`plansData.packages.${metadata.packageId}.short_description`, {
                                year: metadata.taxYear,
                                transactions: parseInt(metadata.totalTransactions) || 0,
                            })
                        );
                        
                        if (metadata.expertReview === 'true') {
                            features.push(
                                t('plansData.addons.expertReview.short_description', {
                                    year: metadata.taxYear,
                                })
                            );
                        }
                    }

                    return features;
                };
                
                // Email notifications removed - Brevo integration disabled
                console.log('[PaymentSuccess] Payment verified successfully');
            } catch (emailErr) {
                console.error('[PaymentSuccess] Error processing payment success:', emailErr);
                // Don't set payment error for non-critical failures
            }
            
            // Invalidate userPlans query to refresh the sidebar
            await reactQueryClient.invalidateQueries({ queryKey: ['userPlans'] });
            
            // Clean URL after processing (remove payment parameters)
            setTimeout(() => {
                navigate('/tax-reports', { replace: true });
            }, 100);
            
        } catch (err) {
            console.error('[PaymentSuccess] Error verifying payment:', err);
            // Remove from verified sessions if payment verification failed
            verifiedSessions.current.delete(sessionId);
            setPaymentError('Failed to verify payment');
        } finally {
            // Clear in-progress flag
            verificationInProgress.current = null;
        }
    }, [apiClient, reactQueryClient, navigate, t]);

    const handlePaymentCancel = useCallback(() => {
        setPaymentStatus('cancel');
        console.log('[PaymentCancel] Payment was cancelled');
        
        // Clean URL after processing
        setTimeout(() => {
            navigate('/tax-reports', { replace: true });
        }, 100);
    }, [navigate]);

    // Payment status detection and handling with deduplication
    useEffect(() => {
        
        const searchParams = new URLSearchParams(location.search);
        const payment = searchParams.get('payment');
        const sessionId = searchParams.get('session_id');

        if (payment === 'success' && sessionId) {
            // Check if this session has already been verified or is currently being verified
            if (verifiedSessions.current.has(sessionId)) {
                console.log('[PaymentSuccess] Session already verified, skipping:', sessionId);
                return;
            }
            
            if (verificationInProgress.current === sessionId) {
                console.log('[PaymentSuccess] Verification already in progress for session:', sessionId);
                return;
            }

            handlePaymentVerification(sessionId);
        } else if (payment === 'cancel') {
            handlePaymentCancel();
        }
    }, [location.search, handlePaymentVerification, handlePaymentCancel]);

    // No blocking loading - always show page structure
    // Handle empty/loading states within the page layout

    return (
        <>
            {/* Payment Result Banner */}
            <PaymentResultBanner 
                status={paymentStatus}
                paymentData={paymentData}
                error={paymentError}
                onDismiss={() => {
                    setPaymentStatus(null);
                    setPaymentData(null);
                    setPaymentError(null);
                }}
            />
            
            <Box sx={{ 
                display: 'flex', 
                gap: { xs: 3, md: 6 }, 
                width: '100%',
                alignItems: 'flex-start',
                // Responsive behavior
                flexDirection: { xs: 'column', md: 'row' },
                py: 2
            }}>
                {/* Left Sidebar - Year Selection */}
                
                    <Box sx={{ 
                        flexShrink: 0,
                        width: { xs: '100%', md: 'auto' }
                    }}>
                        <YearSelectorCards
                            years={yearOptions}
                            selectedYear={selectedYear}
                            onYearSelect={setSelectedYear}
                            yearDataMap={yearDataMap}
                            isLoading={reportsLoading || isRecalculating}
                        />
                    </Box>
               

                {/* Right Content Area */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    {/* Recalculation Alert */}
                    <RecalculationAlert isRecalculating={isRecalculating} />
                    {shouldShowLoading ? (
                        // Show skeleton loading states
                        <Stack spacing={3}>
                            {Array.from({ length: 4 }).map((_, index) => (
                                <Paper
                                    key={`content-skeleton-${index}`}
                                    elevation={0}
                                    sx={{
                                        p: 3,
                                        bgcolor: 'background.paper',
                                        borderRadius: 2,
                                    }}
                                >
                                    <Skeleton width="200px" height="32px" />
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                        <Skeleton width="150px" height="24px" />
                                        <Skeleton width="80px" height="24px" />
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                        <Skeleton width="120px" height="24px" />
                                        <Skeleton width="60px" height="24px" />
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Skeleton width="180px" height="24px" />
                                        <Skeleton width="70px" height="24px" />
                                    </Box>
                                </Paper>
                            ))}
                        </Stack>
                    ) : (
                        <Stack spacing={4}>
                        {/* Unified Tax Summary Box - Shows total and breakdown in one modern component */}
                        <UnifiedTaxSummaryBox
                            totalAmount={reportSummary ? calculateTaxableIncome(reportSummary) : null}
                            details={reportSummary ? extractIncomeBreakdown(reportSummary) : []}
                            isPaid={canLoadDetails}
                            isLoading={isLoadingSelectedYear}
                            selectedYear={selectedYear}
                            issueCount={totalIssueCountForSelectedYear}
                            savingsPotential={totalSavingsPotentialForSelectedYear}
                            costBasisCount={costBasisCountForSelectedYear}
                            missingPriceCount={missingPriceCountForSelectedYear}
                            onGoToSmartCheck={handleNavigateToSmartCheck}
                        />


                        {/* Purchase CTA - Show when locked */}
                        {!canLoadDetails && (
                            <PurchaseActionsBox
                                selectedYear={selectedYear}
                                allYears={yearOptions}
                                isPurchased={canLoadDetails}
                                transactionCounts={{}} // Transaction counts per year (empty for now with lazy loading)
                                onBuyYear={handleBuyYear}
                                onBuyAllYears={handleBuyAllYears}
                                isLoading={isLoadingPlan}
                            />
                        )}

                        {/* Download Files Box - Show locked downloads for additional conversion pressure */}
                        <DownloadFilesBox
                            isPaid={canLoadDetails}
                            onDownloadPdf={() => handleDownloadPdf(`${t('taxReports.fileName')}_${selectedYear}.pdf`)}
                            onDownloadExcel={() => handleDownloadExcel(`${t('taxReports.fileName')}_${selectedYear}.xlsx`)}
                            {...(parseInt(selectedYear) < KAPSO_CUTOFF_YEAR && {
                                onDownloadForms: () => handleDownloadKapsoPdf(`${t('taxReports.fileName')}_Kapso_${selectedYear}.pdf`)
                            })}
                            isPreparingDownload={isDownloadLoading}
                        />

                        {/* Other Transactions Box */}
                        <OtherTransactionsBox
                            otherTransactions={reportSummary ? extractAdditionalTaxData(reportSummary).otherTransactions : []}
                            isPaid={canLoadDetails}
                            isLoading={isLoadingSelectedYear}
                        />
                        
                        {/* Tax Free Income Box */}
                        <TaxFreeIncomeBox
                            taxFreeIncome={reportSummary ? extractAdditionalTaxData(reportSummary).taxFreeIncome : []}
                            isPaid={canLoadDetails}
                            isLoading={isLoadingSelectedYear}
                        />
                    </Stack>
                    )}
                </Box>
            </Box>
        </>
    );
};

export default TaxReportPage;